import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

export const employeeService = {
  getEmployees,
  getEmployee,
  createEmployee,
  updateEmployee,
  uploadEmployeeFile,
  uploadEmployeePhoto,
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

// Upstream returns 1900-01-01 for dates that were never set.
function isUnsetDate(value) {
  return value === null || value === undefined || value === "" || String(value).startsWith("1900-01-01");
}

function formatEmployeeDate(value) {
  if (isUnsetDate(value)) return "-";

  const date =
    value instanceof Date
      ? value
      : typeof value === "string"
        ? parseISO(value)
        : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

// yyyy-MM-dd for <input type="date">, empty when unset.
function toInputDate(value) {
  if (isUnsetDate(value)) return "";
  const date = typeof value === "string" ? parseISO(value) : new Date(value);
  return isValid(date) ? format(date, "yyyy-MM-dd") : "";
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function mapEmployee(raw = {}) {
  const fullName = [raw.first_name, raw.middle_name, raw.last_name]
    .filter(Boolean)
    .join(" ");

  return {
    id: displayValue(raw.employee_id),
    name: displayValue(fullName),
    salutationId: raw.salutation_id ?? null,
    salutationName: displayValue(raw.salutation_name),
    firstName: displayValue(raw.first_name),
    middleName: raw.middle_name ?? "",
    lastName: displayValue(raw.last_name),
    dateOfBirth: formatEmployeeDate(raw.date_of_birth),
    dateOfBirthInput: toInputDate(raw.date_of_birth),
    genderId: raw.gender_id ?? null,
    genderName: displayValue(raw.gender_name),
    nationalityId: raw.nationality_id ?? null,
    nationalId: displayValue(raw.national_id),
    companyMobile: displayValue(raw.company_mobile_number),
    email: displayValue(raw.company_email_address),
    departmentId: raw.department_id ?? null,
    departmentName: displayValue(raw.department_name),
    subDepartmentId: raw.sub_department_id ?? null,
    subDepartmentName: displayValue(raw.sub_department_name),
    designationId: raw.designation_id ?? null,
    designationName: displayValue(raw.designation_name),
    jobLocationId: raw.job_location_id ?? null,
    jobLocationName: displayValue(raw.job_location_name),
    employeeCategoryId: raw.employee_category_id ?? null,
    employeeCategoryName: displayValue(raw.employee_category_name),
    employeeTypeId: raw.employee_type_id ?? null,
    employeeTypeName: displayValue(raw.employee_type_name),
    dateOfJoin: formatEmployeeDate(raw.date_of_join),
    dateOfJoinInput: toInputDate(raw.date_of_join),
    dateOfJoinRaw: raw.date_of_join ?? "",
    statusId: raw.status_id ?? null,
    statusName: displayValue(raw.status_name),
    contractStart: formatEmployeeDate(raw.contract_start_date),
    contractStartInput: toInputDate(raw.contract_start_date),
    contractEnd: formatEmployeeDate(raw.contract_end_date),
    contractEndInput: toInputDate(raw.contract_end_date),
    probationPeriod: displayValue(raw.probation_period),
    noticePeriod: displayValue(raw.notice_period),
    photoUrl: raw.photo_url ?? "",
  };
}

function mapSummary(raw = {}) {
  return {
    totalEmployee: raw.total_employee ?? 0,
    activeEmployee: raw.active_employee ?? 0,
    inactiveEmployee: raw.inactive_employee ?? 0,
    percentageOfLeave: raw.percentage_of_leave ?? 0,
  };
}

function getErrorMessage(error) {
  return error.response?.data?.msg || "Something went wrong";
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data?.result_status)
    ? data.result_status[0]
    : Array.isArray(data)
      ? data[0]
      : data;

  return {
    message:
      messageObject?.message_text ||
      messageObject?.Message_Text ||
      "Saved successfully.",
    refNo: messageObject?.message_ref_no ?? messageObject?.Message_Ref_No ?? null,
  };
}

async function handleRequest(request) {
  try {
    return await request();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

function getEmployees() {
  return handleRequest(async () => {
    const response = await apiClient.get("/hr/employees");
    const data = response?.data ?? {};
    return {
      summary: mapSummary(asArray(data.summary_info)[0]),
      employees: asArray(data.basic_info).map(mapEmployee),
      filterOptions: {
        salutations: asArray(data.salutation_info),
        genders: asArray(data.gender_info),
        nationalities: asArray(data.nationality_info),
        departments: asArray(data.department_info),
        subDepartments: asArray(data.sub_department_info),
        designations: asArray(data.designation_info),
        jobLocations: asArray(data.job_location_info),
        employeeCategories: asArray(data.emp_category_info),
        employeeTypes: asArray(data.emp_type_info),
        statuses: asArray(data.status_info),
      },
    };
  });
}

// The single-employee read has no lookup lists, so nationality is resolved
// from the list call. Returns the mapped employee plus those lists.
async function getEmployee(id) {
  return handleRequest(async () => {
    const [singleResponse, list] = await Promise.all([
      apiClient.get(`/hr/employees/${id}`),
      getEmployees(),
    ]);
    const [raw] = asArray(singleResponse?.data?.basic_info);
    if (!raw) return null;

    const employee = mapEmployee(raw);
    const nationality = list.filterOptions.nationalities.find(
      (option) => String(option.country_id) === String(employee.nationalityId)
    );
    return {
      ...employee,
      nationalityName: displayValue(nationality?.country_name),
      filterOptions: list.filterOptions,
    };
  });
}

function toEmployeeBody(form) {
  return {
    salutation_id: Number(form.salutationId),
    first_name: form.firstName,
    middle_name: form.middleName,
    last_name: form.lastName,
    date_of_birth: form.dateOfBirth,
    nationality_id: Number(form.nationalityId),
    national_id: form.nationalId,
    gender_id: Number(form.genderId),
    company_email_address: form.companyEmail,
    company_mobile_number: form.companyMobile,
    sub_department_id: Number(form.subDepartmentId),
    designation_id: Number(form.designationId),
    job_location_id: Number(form.jobLocationId),
    employee_category_id: Number(form.employeeCategoryId),
    date_of_join: form.dateOfJoin,
    contract_start_date: form.contractStart,
    contract_end_date: form.contractEnd,
    probation_period: Number(form.probationPeriod),
    notice_period: Number(form.noticePeriod),
    photo_url: form.photoUrl || "",
  };
}

function createEmployee(form) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/employees", {
      ...toEmployeeBody(form),
      status_id: Number(form.statusId),
    })
  );
}

function updateEmployee(id, form) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/employees/${id}`, toEmployeeBody(form))
  );
}

// Returns the stored file URL, or "" when no file was sent.
// type is one of: employee-photo, passport, academic-certificate, offer-letter,
// national-id, others.
function uploadEmployeeFile(type, file) {
  return handleRequest(async () => {
    const body = new FormData();
    body.append("file", file);
    const response = await apiClient.post(`/hr/uploads/${type}`, body);
    return response?.data?.url || "";
  });
}

function uploadEmployeePhoto(file) {
  return uploadEmployeeFile("employee-photo", file);
}
